import { memoize } from 'lodash-es';
import { useSyncExternalStore } from 'preact/compat';

import { ANIMAL_ALERT_COOLDOWN_MS, AnimalAlertType } from './constants';

const flaggedAlertTypeByHabitat = new Map<
  string,
  { alertType: AnimalAlertType; timeoutId: ReturnType<typeof setTimeout> }
>();
const flaggedAlertTypeListeners = new Map<string, Set<() => void>>();

export const getFlaggedAnimalAlertType = (habitatId: string | undefined) => {
  if (!habitatId) {
    return undefined;
  }

  return flaggedAlertTypeByHabitat.get(habitatId)?.alertType;
};

const subscribeFlaggedAlertTypeListener = memoize((habitatId: string | undefined) => (listener: () => void) => {
  if (!habitatId) {
    return () => {};
  }

  const listeners = flaggedAlertTypeListeners.get(habitatId) || new Set();
  listeners.add(listener);
  flaggedAlertTypeListeners.set(habitatId, listeners);
  return () => listeners.delete(listener);
});

const getFlaggedAlertTypeSnapshot = memoize((habitatId: string | undefined) => () => getFlaggedAnimalAlertType(habitatId));

export const setFlaggedAnimalAlertType = (habitatId: string, alertType: AnimalAlertType) => {
  if (flaggedAlertTypeByHabitat.has(habitatId)) {
    return;
  }

  const timeoutId = setTimeout(() => {
    flaggedAlertTypeByHabitat.delete(habitatId);
    flaggedAlertTypeListeners.get(habitatId)?.forEach((listener) => listener());
  }, ANIMAL_ALERT_COOLDOWN_MS);

  flaggedAlertTypeByHabitat.set(habitatId, { alertType, timeoutId });
  flaggedAlertTypeListeners.get(habitatId)?.forEach((listener) => listener());
};

export const clearFlaggedAnimalAlertType = (habitatId: string) => {
  const existing = flaggedAlertTypeByHabitat.get(habitatId);
  if (!existing) {
    return;
  }

  clearTimeout(existing.timeoutId);
  flaggedAlertTypeByHabitat.delete(habitatId);
  flaggedAlertTypeListeners.get(habitatId)?.forEach((listener) => listener());
};

export const useFlaggedAnimalAlertType = (habitatId: string | undefined) =>
  useSyncExternalStore(subscribeFlaggedAlertTypeListener(habitatId), getFlaggedAlertTypeSnapshot(habitatId));
